/**
 * Central entitlement hook
 * Source of truth for plan: workspaces.plan (base | pro)
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';

export type PlanTier = 'base' | 'pro';

export interface Entitlements {
  plan: PlanTier;
  isInternal: boolean;
  isPro: boolean;
  hasFullAccess: boolean;
  canBypassPaywall: boolean;
  canBypassTrial: boolean;
  workspaceLimit: number;
  loading: boolean;
}

export function useEntitlements(): Entitlements {
  const { user } = useAuth();
  const { workspaces, activeWorkspaceId, isLoading: workspacesLoading } = useWorkspaceScope();

  const activeWorkspace = activeWorkspaceId
    ? workspaces.find((w) => w.workspace_id === activeWorkspaceId)
    : null;

  const rawPlan = activeWorkspace?.plan;
  const workspacePlan: PlanTier = rawPlan === 'pro' ? 'pro' : 'base';

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile-entitlements', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('id, is_internal, account_status')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        logger.warn('[useEntitlements] Error fetching profile', error);
        return null;
      }

      return data as { id: string; is_internal: boolean | null; account_status: string | null } | null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const isInternal = profile?.is_internal === true;
  const loading = profileLoading || workspacesLoading;

  if (isInternal) {
    return {
      plan: 'pro',
      isInternal: true,
      isPro: true,
      hasFullAccess: true,
      canBypassPaywall: true,
      canBypassTrial: true,
      workspaceLimit: 3,
      loading,
    };
  }

  const workspaceLimit = workspacePlan === 'pro' ? 3 : 1;

  return {
    plan: workspacePlan,
    isInternal: false,
    isPro: workspacePlan === 'pro',
    hasFullAccess: workspacePlan === 'pro',
    canBypassPaywall: false,
    canBypassTrial: false,
    workspaceLimit,
    loading,
  };
}
